/**
 * The Pending changes tray's reads and its open signal (PR 8, B7 + #98),
 * outside PendingTray.tsx so PendingTray.test.tsx can drive them without a
 * DOM (and a component file exports only components).
 *
 * Two reads, deliberately separate: the pending list is what the header
 * count and the drawer act on, and polls (5 s open / 30 s closed). The
 * "Recent changes" history is read ONLY while the drawer is open and that
 * section is expanded — it used to be fetched (status=all, 50 rows) on every
 * poll for a list nobody had opened.
 */
import { apiEntity, OPEN_PENDING_TRAY_EVENT, type ManualEdit } from '../lib/api'
import { SUGGESTS_NOTE_PREFIX } from '../lib/suggestsQueue'

export type RecentScope = 'mine' | 'others' | 'all'

export type PendingRead = {
  items: ManualEdit[]
  /** Pending rows held back because they belong to the Suggests queue. */
  suggestsQueued: number
}

/** The operator's own pending edits. exclude_notes_prefix (PR-B review): a
 *  queued ClickPay Suggests change is applied from the Queued tab and nowhere
 *  else, and is excluded IN THE QUERY so the 200-row cap covers the edits the
 *  operator typed themselves; `excluded_count` is how many were held back. */
export async function loadPendingEdits(operator: string): Promise<PendingRead> {
  const pend = await apiEntity.listManualEdits({
    operator_slug: operator, status: 'pending', exclude_notes_prefix: SUGGESTS_NOTE_PREFIX,
  })
  return { items: pend.items, suggestsQueued: pend.excluded_count ?? 0 }
}

/** The read-only history below the queue (Mine / Others / All). Not filtered
 *  by the Suggests prefix: an applied suggestion belongs in the history. The
 *  operator's own pending rows are already listed above it. */
export async function loadRecentEdits(operator: string, scope: RecentScope): Promise<ManualEdit[]> {
  const rec = await apiEntity.listManualEdits({ operator_slug: operator, status: 'all', scope, limit: 50 })
  return rec.items.filter((r) => !(r.status === 'pending' && r.operator_slug === operator))
}

/** The history is read only while someone is looking at it. */
export function recentQueryEnabled(operator: string | null, drawerOpen: boolean, recentOpen: boolean): boolean {
  return !!operator && drawerOpen && recentOpen
}

/** Open the tray when anything fires OPEN_PENDING_TRAY_EVENT (the bell's
 *  "You have N pending changes" item). Returns the unsubscribe function. */
export function subscribeOpenTray(target: EventTarget, onOpen: () => void): () => void {
  const listener = () => onOpen()
  target.addEventListener(OPEN_PENDING_TRAY_EVENT, listener)
  return () => target.removeEventListener(OPEN_PENDING_TRAY_EVENT, listener)
}

/** "2 waiting · 1 conflict" — the drawer's subtitle. */
export function traySubtitle(waiting: number, conflicts: number): string {
  return `${waiting} waiting · ${conflicts} conflict${conflicts === 1 ? '' : 's'}`
}

/** The header button's accessible name. */
export function trayButtonLabel(count: number): string {
  return `Pending changes: ${count}`
}

const RECENT_OPEN_KEY = 'clickpay.pendingTray.recentOpen'

/** Whether "Recent changes" was expanded earlier in this browser session.
 *  Collapsed by default; storage may be missing or blocked. */
export function readRecentOpen(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(RECENT_OPEN_KEY) === '1'
  } catch {
    return false
  }
}

export function writeRecentOpen(open: boolean): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(RECENT_OPEN_KEY, open ? '1' : '0')
  } catch {
    // A blocked store only means it is collapsed again next time.
  }
}
